<template>
  <div class="chart-simulator">

    <!-- Chart area -->
    <div
      ref="wrapperRef"
      class="chart-simulator__chart-wrapper"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseLeave"
    >
      <div ref="containerRef" class="chart-simulator__canvas" />

      <!-- P&L snapshot lines (draggable) -->
      <template v-if="chartReady">
        <div
          v-for="(ln, idx) in pnlLines"
          :key="ln.offset"
          v-show="ln.y !== null"
          class="sim-level"
          :class="[
            ln.group === 'upper' ? 'sim-level--upper' : 'sim-level--lower',
            draggingLine?.type === 'pnl' && draggingLine?.idx === idx ? 'sim-level--dragging' : '',
          ]"
          :style="{ top: (ln.y ?? 0) + 'px' }"
          @mousedown.prevent="startPnlDrag($event, idx)"
        >
          <span class="sim-level__handle">⠿</span>
          <span class="sim-level__tag">{{ liveFmtOffset(ln.price) }}</span>
          <span class="sim-level__price">${{ Math.round(ln.price).toLocaleString('en-US') }}</span>
          <span class="sim-level__sep">│</span>
          <span class="sim-level__metric">Qty↓: {{ ln.qtyBelow.toFixed(4) }}</span>
          <span class="sim-level__score" :class="ln.pnlBelow >= 0 ? 'clr-green' : 'clr-red'">
            {{ ln.pnlBelow >= 0 ? '+' : '' }}{{ ln.pnlBelow.toFixed(2) }} USD
          </span>
          <span class="sim-level__sep">│</span>
          <span class="sim-level__metric">Spot:</span>
          <span class="sim-level__score" :class="ln.spotPnl >= 0 ? 'clr-green' : 'clr-red'">{{ ln.spotPnl >= 0 ? '+' : '' }}{{ ln.spotPnl.toFixed(2) }}</span>
          <span class="sim-level__metric">Short:</span>
          <span class="sim-level__score" :class="ln.shortPnl >= 0 ? 'clr-green' : 'clr-red'">{{ ln.shortPnl >= 0 ? '+' : '' }}{{ ln.shortPnl.toFixed(2) }}</span>
          <span class="sim-level__metric">Δ:</span>
          <span class="sim-level__score" :class="ln.delta >= 0 ? 'clr-green' : 'clr-red'">{{ ln.delta >= 0 ? '+' : '' }}{{ ln.delta.toFixed(2) }}</span>
        </div>
      </template>

      <!-- ADD+ hover button -->
      <button
        v-if="hoverY !== null && !draggingLine && !simAction.active"
        class="chart-add-btn"
        :style="{ top: hoverY + 'px' }"
        @mousedown.prevent="activateSimAction"
      >+ ADD</button>

      <!-- Sim action form -->
      <div v-if="simAction.active" class="chart-sim-form" :style="{ top: (simAction.y ?? 0) + 'px' }">
        <button class="sim-form__close" @click="simAction.active = false">×</button>
        <span class="sim-form__price">${{ Math.round(simAction.price).toLocaleString('en-US') }}</span>
        <select v-model="simAction.type" class="sim-form__select">
          <option value="openLong">OPEN LONG</option>
          <option value="openShort">OPEN SHORT</option>
        </select>
        <input v-model.number="simAction.qty" type="number" class="sim-form__qty" step="0.001" min="0.001" />
      </div>

    </div>

    <!-- Side panel: current portfolio state -->
    <SimPanel
      :base-head-room="baseHeadRoom"
      :base-coverage="baseCoverage"
      :base-pmp="currentPmp"
      :hedge-avg="currentHedgeAvg"
      :btc-price="btcPrice"
      :spot-pnl="currentSpotPnl"
      :short-pnl="currentShortPnl"
      :delta="currentDelta"
      :sim-result="simActionResult"
    />

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore }   from 'vuex'
import { useChart }   from '@/composables/useChart.js'
import SimPanel       from './SimPanel.vue'

// ─── Portfolio config constants ───────────────────────────────────
const CAPITAL_TOTAL  = 96000
const MAX_MANUAL_PCT = 0.60
const K              = 0.4
const TARGET_MARGIN  = 0.008

// ─── Store ────────────────────────────────────────────────────────
const store = useStore()

// ─── Live portfolio data from store ───────────────────────────────
const manualNotional = computed(() =>
  Object.values(store.state.ledger || {})
    .filter(u => u.bucket === 'manual')
    .reduce((s, u) => s + Number(u.current?.avgPrice || 0) * Number(u.current?.qty || 0), 0)
)
const manualQtySum = computed(() =>
  Object.values(store.state.ledger || {})
    .filter(u => u.bucket === 'manual')
    .reduce((s, u) => s + Number(u.current?.qty || 0), 0)
)
const hedgeNotional = computed(() =>
  Object.values(store.state.shortLedger || {})
    .filter(u => u.status === 'active')
    .reduce((s, u) => s + (u.remainingQty || 0) * (u.origin?.sellPrice || 0), 0)
)
const shortQtySum = computed(() =>
  Object.values(store.state.shortLedger || {})
    .filter(u => u.status === 'active')
    .reduce((s, u) => s + (u.remainingQty || 0), 0)
)

// ─── Derived base state ───────────────────────────────────────────
const currentPmp = computed(() =>
  manualQtySum.value > 0 ? manualNotional.value / manualQtySum.value : 0
)
const currentHedgeAvg = computed(() => {
  const units = Object.values(store.state.shortLedger || {}).filter(u => u.status === 'active')
  const totalOrigQty = units.reduce((s, u) => s + (u.origin?.qty || 0), 0)
  const totalOrigVal = units.reduce((s, u) => s + (u.origin?.sellPrice || 0) * (u.origin?.qty || 0), 0)
  return totalOrigQty > 0 ? totalOrigVal / totalOrigQty : 0
})
const _hf = computed(() =>
  manualNotional.value > 0 ? 1.0 + (hedgeNotional.value / manualNotional.value) * K : 1.0
)
const _max = computed(() => CAPITAL_TOTAL * MAX_MANUAL_PCT * _hf.value)
const baseHeadRoom = computed(() =>
  _max.value > 0 ? ((_max.value - manualNotional.value) / _max.value) * 100 : 0
)
const baseCoverage = computed(() =>
  manualQtySum.value > 0 ? (shortQtySum.value / manualQtySum.value) * 100 : 0
)

// ─── Current portfolio P&L at live BTC price ─────────────────────
const btcPrice         = computed(() => store.state.marketData.btcUsd || 0)
const realizedShortPnl = computed(() =>
  Object.values(store.state.shortLedger || {}).reduce((s, u) => s + (u.realizedPnL || 0), 0)
)
const currentSpotPnl  = computed(() => (btcPrice.value - currentPmp.value) * manualQtySum.value)
const currentShortPnl = computed(() =>
  realizedShortPnl.value +
  Object.values(store.state.shortLedger || {})
    .filter(u => u.status === 'active')
    .reduce((s, u) => s + ((u.origin?.sellPrice || 0) - btcPrice.value) * (u.remainingQty || 0), 0)
)
const currentDelta    = computed(() => currentSpotPnl.value + currentShortPnl.value)

// ─── Local simulate using live store values ───────────────────────
const EMPTY_RESULT = { headRoomAfter: 0, headRoomDelta: 0, coverageAfter: 0, coverageDelta: 0, score: 0 }

function simulate(action, price, qtyVal) {
  const mn   = manualNotional.value
  const mq   = manualQtySum.value
  const hn   = hedgeNotional.value
  const sq   = shortQtySum.value
  const cp   = currentPmp.value
  const ha   = currentHedgeAvg.value
  const bhr  = baseHeadRoom.value
  const bcov = baseCoverage.value

  if (action === 'openLong') {
    const nN = mn + qtyVal * price
    const nQ = mq + qtyVal
    const nHf  = nN > 0 ? 1.0 + (hn / nN) * K : 1.0
    const nMax = CAPITAL_TOTAL * MAX_MANUAL_PCT * nHf
    const nHr  = (nMax - nN) / nMax * 100
    const nCov = sq / nQ * 100
    return { headRoom: bhr, headRoomAfter: nHr, headRoomDelta: nHr - bhr, coverage: bcov, coverageAfter: nCov, coverageDelta: nCov - bcov, pmpAfter: nN / nQ, pmpIsExact: true, score: cp > 0 ? (cp - price) / (cp * TARGET_MARGIN) : 0, scoreRef: 'PMP' }
  }

  if (action === 'closeShort') {
    const nHn  = Math.max(hn - qtyVal * price, 0)
    const nSq  = Math.max(sq - qtyVal, 0)
    const nHf  = mn > 0 ? 1.0 + (nHn / mn) * K : 1.0
    const nMax = CAPITAL_TOTAL * MAX_MANUAL_PCT * nHf
    const nHr  = (nMax - mn) / nMax * 100
    const nCov = mq > 0 ? nSq / mq * 100 : 0
    return { headRoom: bhr, headRoomAfter: nHr, headRoomDelta: nHr - bhr, coverage: bcov, coverageAfter: nCov, coverageDelta: nCov - bcov, pmpAfter: nSq > 0 ? nHn / nSq : 0, pmpIsExact: false, score: ha > 0 ? (ha - price) / (ha * TARGET_MARGIN) : 0, scoreRef: 'HedgeAvg' }
  }

  if (action === 'closeLong') {
    const nN   = mn - qtyVal * price
    const nQ   = mq - qtyVal
    const nHf  = nN > 0 ? 1.0 + (hn / nN) * K : 1.0
    const nMax = CAPITAL_TOTAL * MAX_MANUAL_PCT * nHf
    const nHr  = (nMax - nN) / nMax * 100
    const nCov = nQ > 0 ? sq / nQ * 100 : 0
    return { headRoom: bhr, headRoomAfter: nHr, headRoomDelta: nHr - bhr, coverage: bcov, coverageAfter: nCov, coverageDelta: nCov - bcov, pmpAfter: nQ > 0 ? nN / nQ : 0, pmpIsExact: false, score: cp > 0 ? (price - cp) / (cp * TARGET_MARGIN) : 0, scoreRef: 'PMP' }
  }

  if (action === 'openShort') {
    const nHn  = hn + qtyVal * price
    const nSq  = sq + qtyVal
    const nHf  = mn > 0 ? 1.0 + (nHn / mn) * K : 1.0
    const nMax = CAPITAL_TOTAL * MAX_MANUAL_PCT * nHf
    const nHr  = (nMax - mn) / nMax * 100
    const nCov = mq > 0 ? nSq / mq * 100 : 0
    return { headRoom: bhr, headRoomAfter: nHr, headRoomDelta: nHr - bhr, coverage: bcov, coverageAfter: nCov, coverageDelta: nCov - bcov, pmpAfter: nHn / nSq, pmpIsExact: true, score: ha > 0 ? (price - ha) / (ha * TARGET_MARGIN) : 0, scoreRef: 'HedgeAvg' }
  }

  return null
}

// ─── Chart ────────────────────────────────────────────────────────
const wrapperRef   = ref(null)
const containerRef = ref(null)
const chartReady   = ref(false)
const qty          = ref(0.02)

const { series, currentPrice, init, createSimLines, destroy, updatePmpLine, updateHedgeLine } = useChart(containerRef)

// ─── Sim levels: 1 lower (buy zone) + 1 upper (sell zone) ─────────
const LEVEL_DEFS = [
  { offset: -0.002, group: 'lower', actions: ['openLong',  'closeShort'] },
  { offset:  0.002, group: 'upper', actions: ['closeLong', 'openShort']  },
]

const simLevels = reactive(LEVEL_DEFS.map(d => ({
  ...d,
  price:   0,
  y:       null,
  results: [{ ...EMPTY_RESULT }, { ...EMPTY_RESULT }],
})))

function computeSimLevel(idx) {
  const lvl = simLevels[idx]
  if (lvl.price <= 0) return
  lvl.results = lvl.actions.map(act => simulate(act, lvl.price, qty.value) ?? { ...EMPTY_RESULT })
}

function computeAll() {
  if (!currentPrice.value) return
  simLevels.forEach((lvl, idx) => {
    const isDragging = draggingLine.value?.type === 'sim' && draggingLine.value?.idx === idx
    if (!isDragging) lvl.price = currentPrice.value * (1 + lvl.offset)
    computeSimLevel(idx)
  })
}

// Recompute sim results when store data changes (prices stay where dragged)
watch([manualNotional, manualQtySum, hedgeNotional, shortQtySum], () => {
  simLevels.forEach((_, idx) => computeSimLevel(idx))
  computePnlLines()
})

// Recompute when qty changes
watch(qty, () => simLevels.forEach((_, idx) => computeSimLevel(idx)))

// Keep chart reference lines in sync with live store values
watch(currentPmp,      val => updatePmpLine(val))
watch(currentHedgeAvg, val => updateHedgeLine(val))

// ─── P&L snapshot lines ───────────────────────────────────────────
const pnlLines = reactive([
  { offset: +0.008, group: 'upper', price: 0, y: null, pnl: 0, label: 'Spot P&L',  qtyBelow: 0, pnlBelow: 0, spotPnl: 0, shortPnl: 0, delta: 0 },
  { offset: -0.008, group: 'lower', price: 0, y: null, pnl: 0, label: 'Short P&L', qtyBelow: 0, pnlBelow: 0, spotPnl: 0, shortPnl: 0, delta: 0 },
])

function recomputePnlLine(idx) {
  const manualUnits = Object.values(store.state.ledger || {}).filter(u => u.bucket === 'manual')
  const shortUnits  = Object.values(store.state.shortLedger || {}).filter(u => u.status === 'active')
  const ln = pnlLines[idx]

  // Spot P&L and Short P&L at this line's price (same formula for both lines)
  ln.spotPnl  = (ln.price - currentPmp.value) * manualQtySum.value
  ln.shortPnl = realizedShortPnl.value + shortUnits.reduce((s, u) => s + ((u.origin?.sellPrice || 0) - ln.price) * (u.remainingQty || 0), 0)
  ln.delta    = ln.spotPnl + ln.shortPnl

  if (idx === 0) {
    ln.pnl     = ln.spotPnl
    const below  = manualUnits.filter(u => Number(u.current?.avgPrice || 0) < ln.price)
    ln.qtyBelow  = below.reduce((s, u) => s + Number(u.current?.qty || 0), 0)
    ln.pnlBelow  = below.reduce((s, u) => s + (ln.price - Number(u.current?.avgPrice || 0)) * Number(u.current?.qty || 0), 0)
  } else {
    ln.pnl     = ln.shortPnl
    const above  = shortUnits.filter(u => u.origin.sellPrice > ln.price)
    ln.qtyBelow  = above.reduce((s, u) => s + (u.remainingQty || 0), 0)
    ln.pnlBelow  = above.reduce((s, u) => s + (u.origin.sellPrice - ln.price) * (u.remainingQty || 0), 0)
  }
}

function computePnlLines() {
  if (!currentPrice.value) return
  pnlLines.forEach((ln, idx) => {
    const isDragging = draggingLine.value?.type === 'pnl' && draggingLine.value?.idx === idx
    if (!isDragging) ln.price = currentPrice.value * (1 + ln.offset)
    recomputePnlLine(idx)
  })
}

// ─── Hover button + sim action ───────────────────────────────────────
const hoverY     = ref(null)
const hoverPrice = ref(null)
const simAction  = reactive({ active: false, price: 0, y: 0, type: 'openLong', qty: 0.02 })

function activateSimAction() {
  simAction.active = true
  simAction.price  = hoverPrice.value ?? 0
  simAction.y      = hoverY.value ?? 0
  hoverY.value     = null
}

const simActionResult = computed(() => {
  if (!simAction.active || simAction.qty <= 0 || simAction.price <= 0) return null
  const base = simulate(simAction.type, simAction.price, simAction.qty)
  if (!base) return null

  const sp = simAction.price
  let newSpotPnl, newShortPnl, newPmp, newHedgeAvg

  if (simAction.type === 'openLong') {
    // Scenario: price HAS moved to sp. Buy qty there, then evaluate all P&L at sp.
    const nN = manualNotional.value + simAction.qty * sp
    const nQ = manualQtySum.value + simAction.qty
    newPmp      = nQ > 0 ? nN / nQ : 0
    newSpotPnl  = nQ > 0 ? (sp - newPmp) * nQ : 0
    newShortPnl = realizedShortPnl.value +
      Object.values(store.state.shortLedger || {})
        .filter(u => u.status === 'active')
        .reduce((s, u) => s + ((u.origin?.sellPrice || 0) - sp) * (u.remainingQty || 0), 0)
  } else {
    // openShort: open hedge at sp; spot market stays at live btcPrice.
    const units = Object.values(store.state.shortLedger || {}).filter(u => u.status === 'active')
    const origQty = units.reduce((s, u) => s + (u.origin?.qty || 0), 0)
    const origVal = units.reduce((s, u) => s + (u.origin?.sellPrice || 0) * (u.origin?.qty || 0), 0)
    const nOrigQty = origQty + simAction.qty
    newHedgeAvg = nOrigQty > 0 ? (origVal + simAction.qty * sp) / nOrigQty : 0
    // Evaluate full portfolio AT sp (new short opens at sp → its P&L at entry is 0)
    newSpotPnl  = (sp - currentPmp.value) * manualQtySum.value
    newShortPnl = realizedShortPnl.value +
      units.reduce((s, u) => s + ((u.origin?.sellPrice || 0) - sp) * (u.remainingQty || 0), 0)
  }

  const actionLabel = `${simAction.type} ${simAction.qty} @ $${Math.round(sp).toLocaleString('en-US')}`
  return {
    actionLabel,
    headRoomAfter: base.headRoomAfter, headRoomDelta: base.headRoomDelta,
    coverageAfter: base.coverageAfter, coverageDelta: base.coverageDelta,
    newPmp: newPmp ?? null,
    newHedgeAvg: newHedgeAvg ?? null,
    newSpotPnl, newShortPnl, newDelta: newSpotPnl + newShortPnl,
  }
})

// ─── Drag handling ────────────────────────────────────────────────
const draggingLine = ref(null)  // { type: 'sim' | 'pnl', idx }
const chartSimLines = []

function startDrag(e, idx)    { draggingLine.value = { type: 'sim', idx } }
function startPnlDrag(e, idx) { draggingLine.value = { type: 'pnl', idx } }

function onMouseMove(e) {
  if (!containerRef.value) return
  const rect  = containerRef.value.getBoundingClientRect()
  const y     = e.clientY - rect.top
  if (!draggingLine.value && !simAction.active && series.value) {
    hoverY.value     = y
    hoverPrice.value = series.value.coordinateToPrice(y)
  }
  if (!draggingLine.value || !series.value) return
  const price = series.value.coordinateToPrice(y)
  if (price !== null && price > 0) {
    const { type, idx } = draggingLine.value
    if (type === 'sim') {
      simLevels[idx].price = price
      computeSimLevel(idx)
    } else {
      pnlLines[idx].price = price
      recomputePnlLine(idx)
    }
  }
}

function onMouseUp()    { draggingLine.value = null }
function onMouseLeave() { draggingLine.value = null; if (!simAction.active) hoverY.value = null }

// ─── RAF: sync Y positions ────────────────────────────────────────
let rafId = null
function startRaf() {
  function loop() {
    if (series.value) {
      simLevels.forEach(lvl => {
        const y = series.value.priceToCoordinate(lvl.price)
        lvl.y = (y !== null && y > 0) ? y : null
      })
      pnlLines.forEach(ln => {
        const y = series.value.priceToCoordinate(ln.price)
        ln.y = (y !== null && y > 0) ? y : null
      })
    }
    rafId = requestAnimationFrame(loop)
  }
  loop()
}
function stopRaf() { if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null } }

onMounted(async () => {
  await init(currentPmp.value, currentHedgeAvg.value)
  computeAll()
  computePnlLines()

  chartReady.value = true
  startRaf()
})

onUnmounted(() => { stopRaf(); destroy() })

// ─── Formatting helpers ───────────────────────────────────────────
function liveFmtOffset(price) {
  if (!currentPrice.value || currentPrice.value === 0) return '—'
  const off = (price - currentPrice.value) / currentPrice.value
  return (off >= 0 ? '+' : '') + (off * 100).toFixed(2) + '%'
}
function fmtOffset(o)          { return (o > 0 ? '+' : '') + (o * 100).toFixed(1) + '%' }
function fmtDelta(v)           { return (v >= 0 ? '+' : '') + v.toFixed(2) + 'pp' }
function fmtScore(v)           { return (v >= 0 ? '+' : '') + v.toFixed(2) }
function deltaClass(v)         { return v >= 0 ? 'clr-green' : 'clr-red' }
function coverageDeltaClass(v) { return v === 0 ? '' : v > 0 ? 'clr-green' : 'clr-red' }
function scoreClass(v)         { return v >= 1.0 ? 'clr-green' : v >= 0 ? 'clr-orange' : 'clr-red' }
</script>

<style scoped>
.chart-simulator {
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;
  height: 1100px;
  padding: 12px;
  box-sizing: border-box;
}

.chart-simulator__chart-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: #161b22;
}

.chart-simulator__canvas {
  width: 100%;
  height: 100%;
}

/* ── Sim level overlay ── */
.sim-level {
  position: absolute;
  right: 70px;      /* just left of the price axis (~65px wide) */
  left: auto;
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  pointer-events: none;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 10;
}

/* Draggable sim levels override pointer-events */
.sim-level:not(.pnl-line) {
  pointer-events: auto;
  cursor: ns-resize;
  user-select: none;
}

.sim-level--dragging {
  box-shadow: 0 0 0 1px rgba(255,255,255,0.25);
  z-index: 20;
}

.sim-level__handle {
  font-size: 13px;
  opacity: 0.4;
  cursor: ns-resize;
  margin-right: 2px;
}

/* Lower lines: label sits above the line */
.sim-level--lower {
  background: rgba(243, 156, 18, 0.10);
  border-left: 2px solid rgba(243, 156, 18, 0.7);
  transform: translateY(calc(-100% - 3px));
}

/* Upper lines: label sits below the line */
.sim-level--upper {
  background: rgba(46, 204, 113, 0.10);
  border-left: 2px solid rgba(46, 204, 113, 0.7);
  transform: translateY(3px);
}

.sim-level__tag    { font-size: 11px; opacity: 0.5; min-width: 36px; }
.sim-level__price  { color: #c9d1d9; font-weight: 600; font-size: 12px; }
.sim-level__sep    { opacity: 0.2; margin: 0 2px; }
.sim-level__action { font-size: 11px; opacity: 0.45; text-transform: uppercase; letter-spacing: 0.03em; min-width: 64px; }
.sim-level__metric { color: #c9d1d9; font-size: 12px; }
.sim-level__delta  { font-size: 11px; }
.sim-level__score  { font-weight: 700; font-size: 12px; }

/* ── ADD+ hover button ── */
.chart-add-btn {
  position: absolute;
  left: 8px;
  transform: translateY(-50%);
  background: rgba(99, 102, 241, 0.85);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  z-index: 15;
  pointer-events: auto;
  user-select: none;
  white-space: nowrap;
}
.chart-add-btn:hover { background: #6366f1; }

/* ── Sim action form ── */
.chart-sim-form {
  position: absolute;
  left: 8px;
  transform: translateY(-50%);
  background: #1e1e2e;
  border: 1px solid #4b5563;
  border-radius: 8px;
  padding: 5px 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 15;
  pointer-events: auto;
  white-space: nowrap;
}
.sim-form__close { background: transparent; border: none; color: #9ca3af; font-size: 14px; cursor: pointer; padding: 0 2px; line-height: 1; }
.sim-form__close:hover { color: #f85149; }
.sim-form__price { font-size: 11px; font-weight: 600; color: #c9d1d9; min-width: 70px; }
.sim-form__select,
.sim-form__qty   { padding: 3px 6px; border-radius: 4px; border: 1px solid #4b5563; background: #161b22; color: #c9d1d9; font-size: 11px; }
.sim-form__qty   { width: 80px; }

/* Colors */
.clr-green  { color: #3fb950; }
.clr-orange { color: #e3b341; }
.clr-red    { color: #f85149; }
</style>